export type OutboundRequestFieldMapping =
  | { source: string; maxLength?: number }
  | { sources: string[]; combine: 'join'; separator?: string; maxLength?: number }
  | { value: string };

export const OUTBOUND_MAPPING_MAX_LENGTH = 100000;

export function withPreservedMaxLength(
  next: OutboundRequestFieldMapping | undefined,
  previous: OutboundRequestFieldMapping | undefined,
): OutboundRequestFieldMapping | undefined {
  if (!next || 'value' in next || 'maxLength' in next) return next;
  const previousMax = previous && !('value' in previous) ? previous.maxLength : undefined;
  return previousMax === undefined ? next : { ...next, maxLength: previousMax };
}

export interface OutboundRequestMappingDocument {
  version: 1;
  mappings: Record<string, OutboundRequestFieldMapping>;
  requiredTargets: string[];
}

export function emptyOutboundRequestMappingDocument(): OutboundRequestMappingDocument {
  return { version: 1, mappings: {}, requiredTargets: [] };
}

export function parseOutboundRequestMappingDocument(value: string): OutboundRequestMappingDocument {
  if (!value.trim()) return emptyOutboundRequestMappingDocument();
  const parsed: unknown = JSON.parse(value);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('Request mappings must be a versioned document.');
  const document = parsed as Record<string, unknown>;
  if (document.version !== 1 || !document.mappings || typeof document.mappings !== 'object' || Array.isArray(document.mappings)
    || !Array.isArray(document.requiredTargets)) throw new Error('Request mappings must use version 1 with mappings and requiredTargets.');

  const requiredTargets = document.requiredTargets;
  if (requiredTargets.some(target => typeof target !== 'string' || !target.startsWith('/') || target.length > 1024)
    || new Set(requiredTargets).size !== requiredTargets.length)
    throw new Error('Required targets must be unique JSON Pointers no longer than 1024 characters.');

  const mappings = document.mappings as Record<string, OutboundRequestFieldMapping>;
  return { version: 1, mappings, requiredTargets: [...requiredTargets] as string[] };
}

export function serializeOutboundRequestMappingDocument(document: OutboundRequestMappingDocument): string {
  return JSON.stringify({
    version: 1,
    mappings: document.mappings,
    requiredTargets: Array.from(new Set(document.requiredTargets)),
  });
}

export function setOutboundRequestMapping(
  document: OutboundRequestMappingDocument,
  target: string,
  mapping?: OutboundRequestFieldMapping,
): OutboundRequestMappingDocument {
  const mappings = { ...document.mappings };
  if (mapping === undefined) delete mappings[target];
  else mappings[target] = mapping;
  return { ...document, mappings };
}

export function setOutboundRequestTargetRequired(
  document: OutboundRequestMappingDocument,
  target: string,
  required: boolean,
): OutboundRequestMappingDocument {
  const requiredTargets = new Set(document.requiredTargets);
  if (required) requiredTargets.add(target);
  else requiredTargets.delete(target);
  return { ...document, requiredTargets: Array.from(requiredTargets) };
}

export function stripOutboundRequestSchemaRequired(schemaJson: string): string {
  if (!schemaJson.trim()) return schemaJson;
  const schema: unknown = JSON.parse(schemaJson);
  const visitSchema = (node: unknown) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return;
    const record = node as Record<string, unknown>;
    delete record.required;
    for (const keyword of ['properties', 'patternProperties', '$defs', 'definitions']) {
      const children = record[keyword];
      if (children && typeof children === 'object' && !Array.isArray(children))
        Object.values(children as Record<string, unknown>).forEach(visitSchema);
    }
    for (const keyword of ['items', 'additionalProperties', 'not', 'if', 'then', 'else']) visitSchema(record[keyword]);
    for (const keyword of ['allOf', 'anyOf', 'oneOf', 'prefixItems']) {
      const children = record[keyword];
      if (Array.isArray(children)) children.forEach(visitSchema);
    }
  };
  visitSchema(schema);
  return JSON.stringify(schema, null, 2);
}